import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, CalendarClock, Save, Coins, FileClock, CreditCard, Layers, Wallet, Check, X as XIcon, GitCompareArrows } from 'lucide-react';
import api from '../../lib/api';
import { DisparidadesAlegraDialog } from './DisparidadesAlegraDialog';

const TIPOS = [
  { val: 'contado',    label: 'Contado',           icon: Coins,       color: 'text-green-600' },
  { val: 'cheque',     label: 'Cheque',            icon: FileClock,   color: 'text-blue-600' },
  { val: 'credito',    label: 'Crédito',           icon: CalendarClock, color: 'text-indigo-600' },
  { val: 'escalonado', label: 'Crédito escalonado', icon: Layers,      color: 'text-purple-600' },
  { val: 'tarjeta',    label: 'Tarjeta de Crédito', icon: CreditCard,  color: 'text-pink-600' },
  { val: 'otro',       label: 'Otro',              icon: Wallet,      color: 'text-gray-600' },
];

const FLAGS = [
  { key: 'aparece_en_nomina_viernes',   label: 'Nómina Viernes',    desc: 'Aparece en el listado de pagos a proveedores del viernes (Alegra CxP)' },
  { key: 'va_linea_cheques_por_pagar',  label: 'Línea Cheques',     desc: 'Va al listado especial de Cheques por Pagar (no a la nómina)' },
];

const empty = {
  codigo: '', nombre: '', tipo: 'credito', dias_plazo: 30,
  porcentajes: null, aparece_en_nomina_viernes: true, va_linea_cheques_por_pagar: false,
  orden_alfabetico: 99, descripcion_operacion: '', activo: true,
};

const parsePorcentajes = (str) => {
  if (!str || !str.trim()) return null;
  const nums = str.split(/[,;\s]+/).map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n));
  return nums.length ? nums : null;
};

export default function CondicionesPagoPage() {
  const [condiciones, setCondiciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incluirInactivos, setIncluirInactivos] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [porcentajesStr, setPorcentajesStr] = useState('');
  const [editCodigo, setEditCodigo] = useState(null);
  const [showDisparidades, setShowDisparidades] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/condiciones-pago-crud', { params: { incluir_inactivos: incluirInactivos } });
      setCondiciones(res.data || []);
    } catch {
      toast.error('Error cargando condiciones de pago');
    } finally {
      setLoading(false);
    }
  }, [incluirInactivos]);

  useEffect(() => { load(); }, [load]);

  const filtradas = useMemo(() => {
    let items = condiciones;
    if (filtroTipo !== 'todos') items = items.filter((c) => c.tipo === filtroTipo);
    return items;
  }, [condiciones, filtroTipo]);

  const nuevas = useMemo(() => filtradas.filter((c) => (c.codigo || '').startsWith('CP-')), [filtradas]);
  const historicas = useMemo(() => filtradas.filter((c) => !(c.codigo || '').startsWith('CP-')), [filtradas]);

  const openNew = () => {
    setForm({ ...empty });
    setPorcentajesStr('');
    setEditCodigo(null);
    setShowDialog(true);
  };

  const openEdit = (c) => {
    setForm({
      codigo: c.codigo || '',
      nombre: c.nombre || c.descripcion || '',
      tipo: c.tipo || 'otro',
      dias_plazo: c.dias_plazo ?? 0,
      porcentajes: c.porcentajes || null,
      aparece_en_nomina_viernes: c.aparece_en_nomina_viernes !== false,
      va_linea_cheques_por_pagar: !!c.va_linea_cheques_por_pagar,
      orden_alfabetico: c.orden_alfabetico ?? 99,
      descripcion_operacion: c.descripcion_operacion || '',
      activo: c.activo !== false,
    });
    setPorcentajesStr(Array.isArray(c.porcentajes) ? c.porcentajes.join(', ') : '');
    setEditCodigo(c.codigo);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.codigo.trim() || !form.nombre.trim()) {
      toast.error('Código y nombre son obligatorios');
      return;
    }
    const payload = {
      ...form,
      codigo: form.codigo.trim(),
      nombre: form.nombre.trim(),
      dias_plazo: Number(form.dias_plazo) || 0,
      orden_alfabetico: Number(form.orden_alfabetico) || 99,
      porcentajes: form.tipo === 'escalonado' ? parsePorcentajes(porcentajesStr) : null,
    };
    if (payload.tipo === 'escalonado' && (!payload.porcentajes || payload.porcentajes.length < 2)) {
      toast.error('Para tipo "escalonado" ingresá al menos 2 porcentajes (ej: 50, 50)');
      return;
    }
    try {
      if (editCodigo) {
        await api.put(`/condiciones-pago-crud/${encodeURIComponent(editCodigo)}`, payload);
        toast.success(`"${payload.nombre}" actualizada`);
      } else {
        await api.post('/condiciones-pago-crud', payload);
        toast.success(`"${payload.nombre}" creada`);
      }
      setShowDialog(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleToggleFlag = async (c, flagKey) => {
    if (!(c.codigo || '').startsWith('CP-')) return; // no permitir editar históricas
    const payload = {
      codigo: c.codigo,
      nombre: c.nombre,
      tipo: c.tipo,
      dias_plazo: c.dias_plazo || 0,
      porcentajes: c.porcentajes || null,
      aparece_en_nomina_viernes: c.aparece_en_nomina_viernes !== false,
      va_linea_cheques_por_pagar: !!c.va_linea_cheques_por_pagar,
      orden_alfabetico: c.orden_alfabetico || 99,
      descripcion_operacion: c.descripcion_operacion || '',
      activo: c.activo !== false,
      [flagKey]: !c[flagKey],
    };
    try {
      await api.put(`/condiciones-pago-crud/${encodeURIComponent(c.codigo)}`, payload);
      setCondiciones((prev) => prev.map((x) => (x.codigo === c.codigo ? { ...x, ...payload } : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const handleToggleActive = async (c) => {
    const cod = c.codigo || c.id;
    if (!cod) return;
    try {
      if (c.activo === false) {
        await api.post(`/condiciones-pago-crud/${encodeURIComponent(cod)}/reactivar`);
        toast.success(`Condición reactivada`);
      } else {
        if (!window.confirm(`¿Desactivar "${c.nombre || c.descripcion}"? Las OCs históricas mantienen su referencia.`)) return;
        await api.delete(`/condiciones-pago-crud/${encodeURIComponent(cod)}`);
        toast.success(`Condición desactivada`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const renderPorcentajes = (arr) => {
    if (!arr || !Array.isArray(arr) || !arr.length) return '—';
    return arr.map((p) => `${p}%`).join(' + ');
  };

  const tipoInfo = (t) => TIPOS.find((x) => x.val === t) || TIPOS[TIPOS.length - 1];

  const renderRow = (c, esNueva) => {
    const info = tipoInfo(c.tipo);
    const Icon = info.icon;
    return (
      <TableRow key={c.codigo || c.id} className={c.activo === false ? 'opacity-60' : ''} data-testid={`row-cp-${c.codigo || c.id}`}>
        <TableCell className="font-mono text-xs">{c.codigo || `#${c.id}`}</TableCell>
        <TableCell className="font-medium">{c.nombre || c.descripcion || '—'}</TableCell>
        <TableCell>
          {esNueva ? (
            <div className="flex items-center gap-1.5">
              <Icon className={`h-4 w-4 ${info.color}`} />
              <span className="text-xs">{info.label}</span>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground italic">histórica</span>
          )}
        </TableCell>
        <TableCell className="text-right font-mono text-xs">
          {esNueva ? `${c.dias_plazo || 0} d` : '—'}
        </TableCell>
        <TableCell className="text-xs">
          {esNueva && c.porcentajes ? renderPorcentajes(c.porcentajes) : '—'}
        </TableCell>
        {FLAGS.map((f) => (
          <TableCell key={f.key} className="text-center">
            {esNueva ? (
              <button
                type="button"
                title={`${c[f.key] ? 'Habilitado' : 'Deshabilitado'} — click para cambiar`}
                className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition ${
                  c[f.key]
                    ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-slate-100 text-slate-400 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-500'
                }`}
                onClick={() => handleToggleFlag(c, f.key)}
                disabled={c.activo === false}
                data-testid={`flag-${c.codigo}-${f.key}`}
              >
                {c[f.key] ? <Check className="h-3.5 w-3.5" /> : <XIcon className="h-3.5 w-3.5" />}
              </button>
            ) : (
              <span className="text-slate-300">—</span>
            )}
          </TableCell>
        ))}
        <TableCell className="text-center">
          {c.activo === false ? (
            <Badge variant="outline" className="text-orange-600 border-orange-300">Inactiva</Badge>
          ) : (
            <Badge className="bg-green-600 hover:bg-green-700">Activa</Badge>
          )}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-1">
            {esNueva && (
              <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`btn-edit-${c.codigo}`}>
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleToggleActive(c)}
              className={c.activo === false ? 'text-emerald-600' : 'text-orange-600'}
              data-testid={`btn-toggle-${c.codigo || c.id}`}
            >
              {c.activo === false ? 'Reactivar' : 'Desactivar'}
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-5" data-testid="condiciones-pago-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarClock className="h-6 w-6 text-indigo-600" />
            Condiciones de Pago
          </h1>
          <p className="text-sm text-muted-foreground">
            Cuándo se paga (plazo comercial). No confundir con Medios de Pago (con qué instrumento).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filtroTipo} onValueChange={setFiltroTipo}>
            <SelectTrigger className="w-48" data-testid="filtro-tipo">
              <SelectValue placeholder="Filtrar tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los tipos</SelectItem>
              {TIPOS.map((t) => (
                <SelectItem key={t.val} value={t.val}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={incluirInactivos}
              onChange={(e) => setIncluirInactivos(e.target.checked)}
              className="accent-indigo-600"
              data-testid="chk-incluir-inactivos"
            />
            Mostrar históricas
          </label>
          <Button variant="outline" onClick={() => setShowDisparidades(true)} className="gap-2" data-testid="btn-disparidades-alegra">
            <GitCompareArrows className="h-4 w-4" /> Disparidades Alegra
          </Button>
          <Button onClick={openNew} className="gap-2" data-testid="btn-nueva-condicion">
            <Plus className="h-4 w-4" /> Nueva Condición
          </Button>
        </div>
      </div>
      <DisparidadesAlegraDialog open={showDisparidades} onClose={() => setShowDisparidades(false)} />

      {/* Leyenda */}
      <Card className="bg-slate-50 dark:bg-slate-900/40 border-slate-200">
        <CardContent className="py-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            {FLAGS.map((f) => (
              <div key={f.key}>
                <div className="font-semibold text-slate-700 dark:text-slate-200">{f.label}</div>
                <div className="text-slate-500">{f.desc}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">Cargando…</CardContent></Card>
      ) : (
        <>
          {/* Condiciones NUEVAS activas */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-indigo-600" />
                Condiciones vigentes <Badge variant="outline">{nuevas.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Plazo</TableHead>
                      <TableHead>Cuotas</TableHead>
                      {FLAGS.map((f) => (
                        <TableHead key={f.key} className="text-center text-xs">{f.label}</TableHead>
                      ))}
                      <TableHead className="text-center">Estado</TableHead>
                      <TableHead className="text-right">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nuevas.length === 0 ? (
                      <TableRow><TableCell colSpan={9} className="text-center py-4 text-muted-foreground">
                        No hay condiciones vigentes en este filtro.
                      </TableCell></TableRow>
                    ) : (
                      nuevas.map((c) => renderRow(c, true))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Condiciones HISTÓRICAS */}
          {incluirInactivos && historicas.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-muted-foreground">
                  <FileClock className="h-5 w-5" />
                  Condiciones históricas (heredadas Access) <Badge variant="outline">{historicas.length}</Badge>
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Se mantienen para respetar las OCs históricas. Las nuevas OCs deben usar las condiciones vigentes de arriba.
                </p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID/Código</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="text-right">Plazo</TableHead>
                        <TableHead>Cuotas</TableHead>
                        {FLAGS.map((f) => (
                          <TableHead key={f.key} className="text-center text-xs">{f.label}</TableHead>
                        ))}
                        <TableHead className="text-center">Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historicas.map((c) => renderRow(c, false))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Modal Crear/Editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl" data-testid="dialog-condicion-pago">
          <DialogHeader>
            <DialogTitle>{editCodigo ? 'Editar Condición de Pago' : 'Nueva Condición de Pago'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Código *</Label>
                <Input
                  value={form.codigo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                  placeholder="Ej: CP-CREDITO-45"
                  disabled={!!editCodigo}
                  data-testid="input-codigo"
                />
              </div>
              <div>
                <Label>Tipo *</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="input-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS.map((t) => (
                      <SelectItem key={t.val} value={t.val}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: Crédito 45 días"
                data-testid="input-nombre"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Días de plazo</Label>
                <Input
                  type="number"
                  value={form.dias_plazo}
                  onChange={(e) => setForm({ ...form, dias_plazo: e.target.value })}
                  min="0"
                  data-testid="input-dias"
                />
              </div>
              <div>
                <Label>Orden alfabético</Label>
                <Input
                  type="number"
                  value={form.orden_alfabetico}
                  onChange={(e) => setForm({ ...form, orden_alfabetico: e.target.value })}
                  min="1"
                  data-testid="input-orden"
                />
              </div>
            </div>

            {form.tipo === 'escalonado' && (
              <div>
                <Label>Porcentajes de cada cuota (separados por coma) *</Label>
                <Input
                  value={porcentajesStr}
                  onChange={(e) => setPorcentajesStr(e.target.value)}
                  placeholder="Ej: 50, 50   |   33, 33, 34"
                  data-testid="input-porcentajes"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Cada valor es el % del total a cada plazo. Deben sumar 100.
                </p>
              </div>
            )}

            <div>
              <Label className="text-sm font-semibold">Comportamiento</Label>
              <div className="grid grid-cols-1 gap-2 mt-2 border rounded-md p-3">
                {FLAGS.map((f) => (
                  <label key={f.key} className="flex items-start gap-2 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                      className="mt-1 accent-indigo-600"
                      data-testid={`chk-${f.key}`}
                    />
                    <div>
                      <div className="font-medium">{f.label}</div>
                      <div className="text-xs text-muted-foreground">{f.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>Descripción operativa</Label>
              <Textarea
                value={form.descripcion_operacion}
                onChange={(e) => setForm({ ...form, descripcion_operacion: e.target.value })}
                rows={3}
                placeholder="Ej: Debe aparecer impreso y sumar el plazo a la fecha del DTE…"
                data-testid="input-descripcion"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="btn-cancelar">Cancelar</Button>
            <Button onClick={handleSave} className="gap-2" data-testid="btn-guardar">
              <Save className="h-4 w-4" /> Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
